import React, { useRef } from "react";
import { useServices } from "../../services/Services";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import css from "./Sidebar.module.css";
import CustomReportDialog from "../Dialog/CustomReportDialog";
import Dialog from "../Dialog/Dialog";
import { DialogHandle } from "../Dialog/DialogTypes";
import { Add } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";

interface CustomReportData {
  id: string;
  reportName: string | undefined;
  generatedAt: string | undefined;
}

interface InfiniteQueryPage {
  data: CustomReportData[];
  nextPageToken?: string;
}

interface RequestPayload {
  rpbId: string;
  sessionId: string;
  sessionName: string;
  comment: string | null;
}

const RPB_ID_TO_REPORT_TYPE: Record<string, string> = {
  "2dc94a5b-3036-482f-9a30-b1706a733593": "type_1",
  "d76e9dcf-215f-4ef0-b312-e4c45f49b1be": "type_2",
  "7e9129d3-7f56-453f-b1a4-499f278c5766": "type_3",
};

function parseRequestPayload(
  payload: string | undefined,
): RequestPayload | null {
  if (!payload) return null;
  try {
    return JSON.parse(payload) as RequestPayload;
  } catch {
    return null;
  }
}

function getRequestDisplayName(payload: string | undefined): string {
  const parsed = parseRequestPayload(payload);
  return parsed?.sessionName || "Custom Report";
}

interface SidebarCustomReportSectionProps {
  userId: string;
  isCurrentRoute: (path: string) => boolean;
  onItemSelect: () => void;
}

const SidebarCustomReportSection: React.FC<SidebarCustomReportSectionProps> = ({
  userId,
  isCurrentRoute,
  onItemSelect,
}) => {
  const services = useServices();
  const dialogRef = useRef<DialogHandle>(null);
  const collectionDialogRef = useRef<DialogHandle>(null);
  const createCustomReportRequest =
    services.Request.useCreateCustomReportRequest();
  const createReportCollection = services.Request.useCreateReportCollection();

  const { data: trackedRequests = [] } =
    services.Request.useGetTrackedCustomReportRequests(userId);

  const { data: trackedDyadRequests = [] } =
    services.Request.useGetTrackedDyadReportRequests(userId);

  const {
    data: reportData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = services.CustomReport.useGetCustomReportsInfinite(userId, 10);

  const allCustomReports: CustomReportData[] =
    reportData?.pages?.flatMap((page: InfiniteQueryPage) => page.data) ?? [];

  const newRequests = trackedRequests.filter((r) => r.status === "New");
  const pendingRequests = trackedRequests.filter((r) => r.status === "Pending");
  const failedRequests = trackedRequests.filter((r) => r.status === "Failed");
  const doneRequests = trackedRequests.filter(
    (r) => r.status === "Done" && r.customReportId,
  );

  const newDyadRequests = trackedDyadRequests.filter((r) => r.status === "New");
  const pendingDyadRequests = trackedDyadRequests.filter(
    (r) => r.status === "Pending",
  );
  const failedDyadRequests = trackedDyadRequests.filter(
    (r) => r.status === "Failed",
  );

  const doneReportIds = new Set(doneRequests.map((r) => r.customReportId));
  const customReports = allCustomReports.filter(
    (r) => !doneReportIds.has(r.id),
  );

  const handleRetry = async (payload: string | undefined) => {
    try {
      const parsed = parseRequestPayload(payload);
      if (!parsed) return;
      const reportType = RPB_ID_TO_REPORT_TYPE[parsed.rpbId] ?? parsed.rpbId;
      await createCustomReportRequest.mutateAsync({
        reportType,
        sessionName: parsed.sessionName,
        userId,
        comment: parsed.comment ?? "",
      });
    } catch (error) {
      console.error("Failed to retry custom report request:", error);
    }
  };

  return (
    <>
      <div className={css.generateCombinedReport}>
        <button
          className={css.generateCombinedReportButton}
          onClick={() => collectionDialogRef.current?.show()}
        >
          Generate Combined Report
        </button>
      </div>
      <Dialog
        ref={collectionDialogRef}
        title="Generate Combined Report"
        action={createReportCollection.isPending ? "Generating..." : "Generate"}
        onSubmit={async () => {
          const now = new Date();
          const periodEnd = format(now, "yyyy-MM-dd");
          const periodStart = format(
            new Date(now.getTime() - 31 * 24 * 60 * 60 * 1000),
            "yyyy-MM-dd",
          );
          const monthLabel = format(now, "MMMM yyyy");
          await createReportCollection.mutateAsync({
            bundleTitle: "Unruly Geopolitical Overview",
            bundleSubtitle: monthLabel,
            periodStart,
            periodEnd,
            reportTypes: ["dyad", "geopolitical_trends_and_issues", "regional"],
            userId,
          });
        }}
        onError={() => "Failed to generate combined report. Please try again."}
      >
        <p style={{ marginBottom: 12, lineHeight: 1.5 }}>
          This will generate a combined overview report using the latest version
          of each of the following reports from the last 31 days:
        </p>
        <ul style={{ marginLeft: 20, marginBottom: 12, lineHeight: 1.8 }}>
          <li>
            <strong>Dyad Relationship Assessment</strong>
          </li>
          <li>
            <strong>Geopolitical Trends &amp; Issues</strong>
          </li>
          <li>
            <strong>Regional Developments</strong>
          </li>
        </ul>
        <p style={{ lineHeight: 1.5, color: "#999" }}>
          The combined report will also include a Key Events timeline.
        </p>
      </Dialog>
      <div className={css.sectionTitle}>
        <span className={css.sectionTitleText}>Custom Reports</span>
        <div className={css.sectionTitleAction}>
          <button
            className={css.inlineAddButton}
            onClick={() => dialogRef.current?.show()}
            title="Request Custom Report"
          >
            <Add className={css.inlineAddIcon} /> New
          </button>
        </div>
      </div>
      <CustomReportDialog ref={dialogRef} />
      <div className={css.insightsList}>
        {(newRequests.length > 0 ||
          pendingRequests.length > 0 ||
          failedRequests.length > 0 ||
          doneRequests.length > 0) && (
          <>
            {newRequests.map((request) => {
              const displayName = getRequestDisplayName(request.payload);
              return (
                <div
                  key={request.id}
                  className={`${css.insightItem} ${css.newItem}`}
                >
                  <div className={css.itemContent}>
                    <div className={css.itemTitle} title={displayName}>
                      {displayName}
                    </div>
                  </div>
                </div>
              );
            })}
            {pendingRequests.map((request) => {
              const displayName = getRequestDisplayName(request.payload);
              return (
                <div
                  key={request.id}
                  className={`${css.insightItem} ${css.pendingItem}`}
                >
                  <div className={css.itemContent}>
                    <div className={css.itemTitle} title={displayName}>
                      {displayName}
                    </div>
                  </div>
                  <div className={css.loadingSpinner}>
                    <CircularProgress size={16} />
                  </div>
                </div>
              );
            })}
            {doneRequests.map((request) => {
              const path = `/custom-report/${request.customReportId}`;
              const displayName = getRequestDisplayName(request.payload);
              return (
                <Link
                  key={request.id}
                  to={path}
                  onClick={onItemSelect}
                  className={`${css.insightItem} ${isCurrentRoute(path) ? css.currentRoute : ""}`}
                >
                  <div className={css.itemContent}>
                    <div className={css.itemTitle} title={displayName}>
                      {displayName}
                    </div>
                  </div>
                  <div className={css.itemDate}>
                    {request.resolvedAt
                      ? format(new Date(request.resolvedAt), "MMM dd")
                      : "Recent"}
                  </div>
                </Link>
              );
            })}
            {(newRequests.length > 0 ||
              pendingRequests.length > 0 ||
              failedRequests.length > 0) && (
              <div className={css.sectionSeparator} />
            )}
            {failedRequests.map((request) => {
              const displayName = getRequestDisplayName(request.payload);
              return (
                <div
                  key={request.id}
                  className={`${css.insightItem} ${css.failedItem}`}
                >
                  <div className={css.itemContent}>
                    <div className={css.itemTitle} title={displayName}>
                      {displayName}
                    </div>
                  </div>
                  <button
                    className={css.retryButton}
                    onClick={() => handleRetry(request.payload)}
                    disabled={createCustomReportRequest.isPending}
                  >
                    Retry
                  </button>
                </div>
              );
            })}
          </>
        )}
        {(newDyadRequests.length > 0 ||
          pendingDyadRequests.length > 0 ||
          failedDyadRequests.length > 0) && (
          <>
            {newDyadRequests.map((request) => (
              <div
                key={request.id}
                className={`${css.insightItem} ${css.newItem}`}
              >
                <div className={css.itemContent}>
                  <div className={css.itemTitle} title="Dyad Report">
                    Dyad Report
                  </div>
                </div>
              </div>
            ))}
            {pendingDyadRequests.map((request) => (
              <div
                key={request.id}
                className={`${css.insightItem} ${css.pendingItem}`}
              >
                <div className={css.itemContent}>
                  <div className={css.itemTitle} title="Dyad Report">
                    Dyad Report
                  </div>
                </div>
                <div className={css.loadingSpinner}>
                  <CircularProgress size={16} />
                </div>
              </div>
            ))}
            {failedDyadRequests.map((request) => (
              <div
                key={request.id}
                className={`${css.insightItem} ${css.failedItem}`}
              >
                <div className={css.itemContent}>
                  <div className={css.itemTitle} title="Dyad Report">
                    Dyad Report
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {customReports.map((report) => {
          const path = `/custom-report/${report.id}`;
          const title = report.reportName || "Custom Report";
          return (
            <Link
              key={report.id}
              to={path}
              onClick={onItemSelect}
              className={`${css.insightItem} ${isCurrentRoute(path) ? css.currentRoute : ""}`}
            >
              <div className={css.itemContent}>
                <div className={css.itemTitle} title={title}>
                  {title}
                </div>
              </div>
              <div className={css.itemDate}>
                {report.generatedAt
                  ? format(new Date(report.generatedAt), "MMM dd")
                  : "Recent"}
              </div>
            </Link>
          );
        })}
        {hasNextPage && (
          <button
            className={css.loadMoreButton}
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Loading..." : "Load More"}
          </button>
        )}
      </div>
    </>
  );
};

export default SidebarCustomReportSection;
